import { formatSurface } from '../../../../../common-utils/formatters';
import type { ContactInteractionType, InteractionFormData } from '../../../../@types/contactInteraction';
import type { DPE } from '../../../../@types/DPE';
import { DPE_STEPS } from '../../../../@types/DPE';
import { JWTAuthorization, JWTHeader } from '../../../../utils/JWTHeader';
import { showToast } from '../../../common/Notification';

interface MonthFiltered {
  type?: 'reception' | 'etablissement';
  date?: string;
}

export const fetchDpesData = async(
  companyId: number,
  jwt: string,
  monthFiltered?: MonthFiltered,
  onlyTreated = false,
) => {
  const fetchDpes = async(step: string) => {
    let apiUrl;
    if (monthFiltered && monthFiltered.type === 'reception' && monthFiltered.date) {
      apiUrl = `/api/companies/${companyId}/dpes?step=1&limit=10000000&filterDPEStep=${step}&filterMonthCreatedAt=${monthFiltered.date}`;
    } else if (monthFiltered && monthFiltered.type === 'etablissement' && monthFiltered.date) {
      apiUrl = `/api/companies/${companyId}/dpes?step=1&limit=10000000&filterDPEStep=${step}&filterMonthEstablishedAt=${monthFiltered.date}`;
    } else {
      // standard fetch no filtering
      apiUrl = `/api/companies/${companyId}/dpes?step=1&limit=10000000&filterDPEStep=${step}`;
    }
    const response = await fetch(apiUrl, JWTHeader(jwt));
    const result = await response.json();
    return result.results;
  };

  let dpesInProcess: Awaited<ReturnType<typeof fetchDpes>> = [];
  let dpesProcessed: Awaited<ReturnType<typeof fetchDpes>> = [];

  if (onlyTreated) {
    dpesInProcess = await fetchDpes(DPE_STEPS.IN_PROCESS);
    // filter out the owners that don't have an interaction
    for (let i = 0; i < dpesInProcess.length; i++) {
      dpesInProcess[i].owners = dpesInProcess[i].owners.filter((owner) => owner.interaction);
    }
    // if the dpe has no more owners inside, remove it from the list
    dpesInProcess = dpesInProcess.filter((dpe) => dpe.owners.length > 0);
    dpesProcessed = await fetchDpes(DPE_STEPS.PROCESSED);
  } else {
    dpesInProcess = await fetchDpes(DPE_STEPS.IN_PROCESS);
    // filter out the owners that have an interaction
    for (let i = 0; i < dpesInProcess.length; i++) {
      dpesInProcess[i].owners = dpesInProcess[i].owners.filter((owner) => !owner.interaction);
    }
  }

  // Combine results if onlyTreated is true
  const allDpes = onlyTreated ? [...dpesInProcess, ...dpesProcessed] : dpesInProcess;

  // Process the results
  return allDpes.map((dpe) => {
    // Filter owners based on onlyTreated flag
    const owners = dpe.owners
      .map((owner) => ({
        id: owner.id,
        firstname: owner.firstname,
        lastname: owner.lastname,
        address: owner.address,
        livingIn: owner.livingIn,
        phone: owner.phone,
        email: owner.email,
        contacts: owner.contacts,
        interaction: owner.interaction,
        showPhone: false, // Add a flag to show/hide phone number
        civility: owner.civility,
        floorNumber: owner.floorNumber,
        doorNumber: owner.doorNumber,
        lotNumber: owner.lotNumber,
        postalCode: owner.postalCode,
        city: owner.city,
        surface: owner.surface,
      }));

    // Return the processed DPE data
    return {
      id: dpe.id,
      coproId: dpe.copro.id,
      coproName: dpe.copro.name,
      coproAddress: dpe.details.banLabel,
      assistant: `${dpe.treatedBy.firstname} ${dpe.treatedBy.lastname ? dpe.treatedBy.lastname.toUpperCase() : ''}`,
      value: dpe.details.classeBilanDpe ? dpe.details.classeBilanDpe : 'Non renseigné',
      rawDate: dpe.details.dateReceptionDpe,
      dateDPE: `${new Date(dpe.details.dateReceptionDpe).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })}`,
      rawSurface: dpe.details.surfaceHabitableLogement,
      surfaceDPE: dpe.details.surfaceHabitableLogement ? formatSurface(Number(dpe.details.surfaceHabitableLogement)) : 'Non renseignée',
      floorDPE: dpe.details.complEtageAppartement,
      infoDPE: dpe.details.complRefLogement ? dpe.details.complRefLogement : '',
      owners,
      details: dpe.details,
    };
  });
};

export const handleContactClick = async(ownerId: number, type: ContactInteractionType, jwt: string) => {
  try {
    const response = await fetch(`/api/owners/${ownerId}/contacts`, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: JWTAuthorization(jwt),
      },
      body: JSON.stringify({ type }),
    });

    if (!response.ok) {
      console.error('Error creating contact :', response.statusText);
      showToast('error', 'Une erreur est survenue lors de la sauvegarde de la prise de contact.');
      return;
    }

    const data = await response.json();
    showToast('success', 'Prise de contact enregistrée avec succès.');
    return data;
  } catch (error) {
    console.error('Error creating contact :', error);
    showToast('error', 'Une erreur est survenue lors de la sauvegarde de la prise de contact.');
  }
};

export const handleFormSubmit = async(
  ownerId: number,
  dpe: DPE,
  interactionForm: InteractionFormData & { dpeInitiator?: number },
  jwt: string,
) => {
  await fetch(`/api/owners/${ownerId}/interactions`, {
    method: 'POST',
    headers: {
      accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: JWTAuthorization(jwt),
    },
    body: JSON.stringify(interactionForm),
  }).then(() => {
    showToast('success', 'Le propriétaire a été traité.');
  }).catch((error) => {
    console.error('Error creating interaction :', error);
    showToast('error', 'Une erreur est survenue lors de la mise à jour des données.');
  });

  // check if the other owners either all have an interaction or this one is the initiator
  const allOwnersHaveInteraction = dpe.owners.every((owner) => owner.interaction || owner.id === ownerId);
  if (allOwnersHaveInteraction || interactionForm.dpeInitiator == 1) {
    await fetch(`/api/dpes/${dpe.id}`, {
      method: 'PATCH',
      headers: {
        Authorization: JWTAuthorization(jwt),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ step: DPE_STEPS.PROCESSED }),
    }).catch((error) => {
      console.error('Error next step dpe :', error);
      showToast('error', 'Une erreur est survenue lors de la mise à jour des données.');
    });
  }
};
